/**
 * V70 Phase 3-C — 엔글 마블 서버 serverApi
 *
 * 기존 Mock (../mock/serverApi.ts) 과 동일한 MarbleServerApi interface 구현.
 * 모든 상태·사용량·점수를 BE Callable 기준으로 가져옴.
 *
 * 매핑 규칙 (BE snapshotPlayerState → FE MyState):
 *   - BE snapshot: uid, epoch, position, totalScore, items, isTrapped, trapFails,
 *                  lapsCompleted, lapsReward, playCount, highScoreEvents, lossSum,
 *                  lifetimeRollSeq, inProgressEvent, currentShopVisit, version
 *   - FE 추가 필드:
 *       displayName, orgUnitId — auth 유저 매개변수
 *       updatedAt — Date.now() (서버 Timestamp 는 ms 변환 복잡 → FE 로컬 ms)
 *       diceToday / diceUsedToday / diceDateYmd — **표시용 로컬 counter**
 *         (BE 는 `getDailyDiceStatus` Callable 미제공. 공식 플레이 제한은 서버가 결정)
 *
 * 한계:
 *   1. 당일 사용량 표시: 로컬 counter — localStorage 삭제 시 표시만 리셋.
 *      실제 플레이 가능 여부는 서버 (resource-exhausted error) 가 결정.
 *   2. E9 워프 선택: BE marble.ts (Phase 3-C.1 결함 B 수정) 로 완전 지원. 체인 (E9 → E8/E10/VAULT/SHOP/safe 등) 포함.
 *   3. shop 구매: BE 가 inProgressEvent 를 clear 안 함 — 응답 snapshot 신뢰.
 */
import {
  callMarbleRollDice,
  callMarbleSubmitEventChoice,
  callMarblePurchaseItem,
  callGetActiveMarbleSession,
  callGetPublicTournamentLeaderboard,
  callGetTournamentDailyStatus,
  callQaMarbleResetMyState,
  callQaMarbleRefillDice,
  type MarbleSnapshotPlayerState,
  type MarbleRollResp,
  type MarblePurchaseItemKey,
  type InProgressEventStateServer,
} from '../../shared/tournamentPlayCallables';
import {
  callGetPublicTournamentGameSettings,
  callUpdateTournamentDicePolicy,
  callUpdateTournamentOpenPeriod,
  type PublicGameSettingsResp,
} from '../../shared/tournamentGameApi';
import type {
  BoardCell, LeaderboardRow, MarbleBoard, MarbleSettings, MyState,
  RollDiceRequest, RollDiceResponse,
  SubmitEventChoiceRequest, SubmitEventChoiceResponse,
  DicePolicy, DailyDiceStatus,
  PurchaseItemRequest, PurchaseItemResponse,
  InProgressEventState,
} from '../engine/types';
import { DEFAULT_DICE_POLICY } from '../engine/types';
import { MARBLE_GLOBAL_CONFIG, V2_SAFE_ROLES, SHOP_CONFIG } from '../config/eventConfig';
import { generateBoard } from '../engine/boardLayout';
import type { EventKind } from '../config/eventConfig';
import type { MarbleServerApi } from '../mock/serverApi';

// Phase 3-C.1 결함 D 수정 — 로컬 counter 전면 제거. 서버 getTournamentDailyStatus 가 공식 기준.
// (이전 S9 설계: localStorage counter — 사용자 지시 §D "로컬 카운터를 공식 잔여 횟수 근거로 사용 금지")

// ─────────── BE snapshot → FE MyState 매핑 ───────────

// Phase 3-C.1 결함 D 수정 — snapshotToMyState / emptyMyState 는 서버 조회 결과를 매개변수로 받음.
// 로컬 counter 완전 제거. 호출 측이 callGetTournamentDailyStatus 를 먼저 조회해 전달.

interface ServerDailyStatus {
  used: number;
  diceDateYmd: string;
  remaining: number | null;
}

function snapshotToMyState(
  snap: MarbleSnapshotPlayerState,
  displayName: string,
  orgUnitId: string | undefined,
  policy: DicePolicy,
  dailyStatus: ServerDailyStatus,
): MyState {
  const diceToday = policy.mode === 'limited'
    ? (dailyStatus.remaining ?? Math.max(0, policy.dailyLimit - dailyStatus.used))
    : 999;
  return {
    uid: snap.uid,
    displayName,
    orgUnitId,
    position: snap.position,
    totalScore: snap.totalScore,
    diceToday,
    diceUsedToday: dailyStatus.used,
    diceDateYmd: dailyStatus.diceDateYmd,
    inProgressEvent: snap.inProgressEvent as InProgressEventState | null,
    playCount: snap.playCount,
    highScoreEvents: snap.highScoreEvents,
    lossSum: snap.lossSum,
    epoch: snap.epoch,
    updatedAt: Date.now(),
    items: snap.items,
    isTrapped: snap.isTrapped,
    trapFails: snap.trapFails,
    lapsCompleted: snap.lapsCompleted,
    lapsReward: snap.lapsReward,
  };
}

function emptyMyState(uid: string, displayName: string, orgUnitId: string | undefined, policy: DicePolicy, epoch: number, dailyStatus: ServerDailyStatus): MyState {
  return {
    uid, displayName, orgUnitId,
    position: 0, totalScore: 0,
    diceToday: policy.mode === 'limited'
      ? (dailyStatus.remaining ?? Math.max(0, policy.dailyLimit - dailyStatus.used))
      : 999,
    diceUsedToday: dailyStatus.used, diceDateYmd: dailyStatus.diceDateYmd,
    inProgressEvent: null,
    playCount: 0, highScoreEvents: 0, lossSum: 0,
    epoch, updatedAt: Date.now(),
    items: { shields: 0, speedBoosts: 0 },
    isTrapped: false, trapFails: 0,
    lapsCompleted: 0, lapsReward: 0,
  };
}

function toDicePolicy(pub: PublicGameSettingsResp): DicePolicy {
  if (pub.dicePolicy && (pub.dicePolicy.mode === 'limited' || pub.dicePolicy.mode === 'unlimited')) {
    const n = Math.max(1, Math.min(99, Math.floor(Number(pub.dicePolicy.dailyLimit) || DEFAULT_DICE_POLICY.dailyLimit)));
    return { mode: pub.dicePolicy.mode, dailyLimit: n };
  }
  return { ...DEFAULT_DICE_POLICY };
}

// shop pending.kind 에서 선택지 → marblePurchaseItem.item 매핑
function isShopChoice(choice: unknown): choice is MarblePurchaseItemKey {
  return choice === 'shield' || choice === 'speedBoost' || choice === 'pass';
}

// snapshot 변화 로부터 arrivalKind 재구성 (BE 응답에 없음)
function inferArrivalKind(
  snap: MarbleSnapshotPlayerState,
): 'score' | 'shield' | 'trap' | 'start' | 'pending' | 'none' | undefined {
  if (snap.inProgressEvent) return 'pending';
  if (snap.isTrapped) return 'trap';
  if (snap.position === V2_SAFE_ROLES.START) return 'start';
  return undefined;
}

// ─────────── MarbleServerApi 구현 ───────────

const DEFAULT_SETTINGS: MarbleSettings = {
  enabled: false,
  diceDailyLimit: MARBLE_GLOBAL_CONFIG.diceDailyLimit,
  eventStartAt: null,
  eventEndAt: null,
  gracePeriodMinutes: MARBLE_GLOBAL_CONFIG.gracePeriodMinutes,
  leaderboardCacheTtlSec: MARBLE_GLOBAL_CONFIG.leaderboardCacheTtlSec,
  leaderboardEpoch: 1,
  officialRankingStatus: 'IN_PROGRESS',
};

async function fetchSettingsAndPolicy(): Promise<{ settings: MarbleSettings; policy: DicePolicy }> {
  const pub = await callGetPublicTournamentGameSettings('marble');
  const policy = toDicePolicy(pub);
  const settings: MarbleSettings = {
    ...DEFAULT_SETTINGS,
    enabled: pub.enabled,
    diceDailyLimit: policy.dailyLimit,
    eventStartAt: pub.openStartAt ? Date.parse(pub.openStartAt) : null,
    eventEndAt: pub.openEndAt ? Date.parse(pub.openEndAt) : null,
    gracePeriodMinutes: pub.gracePeriodMinutes ?? MARBLE_GLOBAL_CONFIG.gracePeriodMinutes,
    leaderboardEpoch: Number.isFinite(pub.leaderboardEpoch) && pub.leaderboardEpoch > 0
      ? pub.leaderboardEpoch
      : DEFAULT_SETTINGS.leaderboardEpoch,
  };
  return { settings, policy };
}

export const serverMarbleApi: MarbleServerApi = {
  async getSettings() {
    const { settings } = await fetchSettingsAndPolicy();
    return settings;
  },

  async getBoard() {
    const { settings } = await fetchSettingsAndPolicy();
    // 보드는 서버와 동일 알고리즘 (generateBoard(epoch)) — deterministic
    return { epoch: settings.leaderboardEpoch, cells: generateBoard(settings.leaderboardEpoch) };
  },

  async getMyState(uid, displayName, orgUnitId) {
    const [{ policy, settings }, active, daily] = await Promise.all([
      fetchSettingsAndPolicy(),
      callGetActiveMarbleSession(),
      callGetTournamentDailyStatus({ gameKey: 'marble' }),
    ]);
    const dailyStatus: ServerDailyStatus = { used: daily.used, diceDateYmd: daily.ymd, remaining: daily.remaining };
    if (!active.playerState) {
      return emptyMyState(uid, displayName, orgUnitId, policy, settings.leaderboardEpoch, dailyStatus);
    }
    return snapshotToMyState(active.playerState, displayName, orgUnitId, policy, dailyStatus);
  },

  async rollDice(_uid, req) {
    const resp: MarbleRollResp = await callMarbleRollDice({
      clientRequestId: req.clientRequestId,
      useSpeedBoost: req.useSpeedBoost === true,
    });
    // Phase 3-C.1 결함 D 수정 — 로컬 counter 제거. 서버 getTournamentDailyStatus 가 공식 기준.
    const rr = resp.rollResult;
    const out: RollDiceResponse = {
      rollId: rr.rollId,
      dice: rr.dice,
      from: rr.from,
      to: rr.to,
      cellKind: rr.cellKind as EventKind,
      immediate: rr.immediate ? {
        delta: rr.immediate.delta,
        scoreAfter: rr.immediate.scoreAfter,
        shieldConsumed: rr.immediate.shieldConsumed,
        label: rr.immediate.label,
        grossLoss: rr.immediate.grossLoss,
        shieldsCapReached: rr.immediate.shieldsCapReached,  // QA Issue #4
      } : undefined,
      pending: rr.pending as InProgressEventState | undefined,
      lapsReward: rr.lapsReward,
      resetDuringPlay: resp.wasReset || undefined,
      isMock: false,
    };
    return out;
  },

  async submitEventChoice(_uid, req) {
    // Shop pending 처리: choice 가 shield/speedBoost/pass 중 하나면 marblePurchaseItem 라우팅
    if (isShopChoice(req.choice)) {
      const pr = await callMarblePurchaseItem({
        clientRequestId: req.clientRequestId,
        item: req.choice,
      });
      // marblePurchaseItem 응답을 SubmitEventChoiceResponse 로 변환
      let outcomeLabel: string;
      let delta: number;
      if (req.choice === 'pass') {
        outcomeLabel = '상점 통과';
        delta = 0;
      } else if (req.choice === 'shield') {
        outcomeLabel = `🛡 방패 구매 (${pr.shieldsAfter}/${SHOP_CONFIG.shieldMax})`;
        delta = -pr.priceDeducted;
      } else {
        outcomeLabel = `🚀 가속 구매 (${pr.speedBoostsAfter}/${SHOP_CONFIG.speedMax})`;
        delta = -pr.priceDeducted;
      }
      const out: SubmitEventChoiceResponse = {
        eventId: req.eventId,
        delta,
        scoreAfter: pr.scoreAfter,
        outcomeLabel,
        isMock: false,
      };
      return out;
    }
    // E9 워프 — BE marble.ts Phase 3-C.1 결함 B 수정으로 완전 지원 (워프 체인 포함).
    const resp = await callMarbleSubmitEventChoice({
      clientRequestId: req.clientRequestId,
      eventId: req.eventId,
      choice: req.choice,
    });
    const snap = resp.playerStateSnapshot;
    // QA Issue #3: BE가 arrivalCellKind 를 명시한 경우 → arrivalKind 매핑 (E10→score, E8→shield, safe→start/none)
    // inferArrivalKind 는 playerStateSnapshot 만 보고 추론해서 E10 destination 을 'score' 로 못 잡음 — arrivalCellKind 우선.
    const explicitArrival: SubmitEventChoiceResponse['arrivalKind'] | undefined =
      resp.arrivalCellKind === 'E10' ? 'score'
        : resp.arrivalCellKind === 'E8' ? 'shield'
          : resp.arrivalCellKind === 'safe' ? 'none'
            : undefined;
    const out: SubmitEventChoiceResponse = {
      eventId: resp.eventId,
      delta: resp.delta,
      scoreAfter: resp.scoreAfter,
      shieldConsumed: resp.shieldConsumed || undefined,
      grossLoss: resp.grossLoss,
      outcomeLabel: resp.outcomeLabel,
      arrivalKind: explicitArrival ?? inferArrivalKind(snap),
      shieldsOwnedAfter: snap.items.shields,
      drawnNumber: resp.drawnNumber,
      arrivalCellKind: resp.arrivalCellKind,  // QA Issue #3 FE 전파
      shieldsCapReached: resp.shieldsCapReached,  // QA Issue #4 FE 전파
      isMock: false,
    };
    return out;
  },

  async purchaseItem(_uid, req) {
    const pr = await callMarblePurchaseItem({
      clientRequestId: req.clientRequestId,
      item: req.item,  // shield | speedBoost (FE 타입은 pass 제외)
    });
    const out: PurchaseItemResponse = {
      success: true,
      item: req.item,
      priceDeducted: pr.priceDeducted,
      scoreAfter: pr.scoreAfter,
      itemsAfter: { shields: pr.shieldsAfter, speedBoosts: pr.speedBoostsAfter },
      isMock: false,
    };
    return out;
  },

  async getLeaderboard(selfUid) {
    const lb = await callGetPublicTournamentLeaderboard({ gameKey: 'marble', limit: 10 });
    const rows: LeaderboardRow[] = lb.rows.map((r) => ({
      rank: r.rank,
      uid: r.uid,
      displayName: r.displayName,
      orgUnitId: r.orgUnitId ?? undefined,
      totalScore: r.bestScore,
      playCount: r.playCount,
      highScoreEvents: r.marble?.highScoreEvents ?? 0,
      lossSum: r.marble?.lossSum ?? 0,
    }));
    const self: LeaderboardRow | null = lb.self ? {
      rank: lb.self.rank,
      uid: lb.self.uid,
      displayName: lb.self.displayName,
      orgUnitId: lb.self.orgUnitId ?? undefined,
      totalScore: lb.self.bestScore,
      playCount: lb.self.playCount,
      highScoreEvents: lb.self.marble?.highScoreEvents ?? 0,
      lossSum: lb.self.marble?.lossSum ?? 0,
    } : null;
    // selfUid 가 응답 self 와 불일치 시 (서버가 다른 세션 uid 로 조회 — 거의 발생 X): self 그대로 신뢰
    void selfUid;
    return { rows, self, cachedAt: Date.now() };
  },

  async qaResetMyState(_uid, clientRequestId) {
    if (!clientRequestId) throw new Error('clientRequestId 누락 (QA Reset).');
    await callQaMarbleResetMyState({ clientRequestId });
    return { ok: true };
  },

  async qaRefillDice(_uid, _dice, clientRequestId) {
    // _dice 는 서버에서 무시 — BE 가 고정 +10 지급 (사용자 지시: 클라이언트 수량 지정 불가).
    if (!clientRequestId) throw new Error('clientRequestId 누락 (QA Refill).');
    await callQaMarbleRefillDice({ clientRequestId });
    return { ok: true };
  },

  async qaResetAll() {
    throw new Error('서버 모드에서는 QA 전역 리셋이 비활성화됩니다. 본인 리셋만 지원됩니다.');
  },

  async updateOpenPeriod(req) {
    const start = req.openStartAt;
    const end = req.openEndAt;
    if (start != null && end != null && start >= end) {
      throw new Error('종료 일시는 시작 일시보다 뒤여야 합니다.');
    }
    await callUpdateTournamentOpenPeriod(
      'marble',
      start != null ? new Date(start).toISOString() : null,
      end != null ? new Date(end).toISOString() : null,
    );
    return { success: true, openStartAt: start, openEndAt: end, isMock: false };
  },

  async getDicePolicy() {
    const { policy } = await fetchSettingsAndPolicy();
    return policy;
  },

  async getDailyDiceStatus(_uid) {
    // Phase 3-C.1 결함 D 수정 — 서버 getTournamentDailyStatus 로 교체. 로컬 counter 완전 제거.
    const r = await callGetTournamentDailyStatus({ gameKey: 'marble' });
    return {
      mode: r.mode,
      used: r.used,
      limit: r.limit,
      remaining: r.remaining,
      diceDateYmd: r.ymd,
      weekendCarryover: r.weekendCarryover === true,
    };
  },

  async updateDicePolicy(req) {
    const prev = await serverMarbleApi.getDicePolicy();
    await callUpdateTournamentDicePolicy({ mode: req.policy.mode, dailyLimit: req.policy.dailyLimit });
    return {
      success: true,
      previous: prev,
      next: req.policy,
      changedAt: Date.now(),
      isMock: false,
    };
  },

  async getDicePolicyHistory(_opts) {
    return [];  // 서버 모드에서 변경 이력은 admin 콜러블 통해 조회
  },
};

// board cells 타입 호환 (unused dependency 제거용 — 보드 로컬 생성 참고)
export type _MarbleBoardType = MarbleBoard;
export type _MarbleBoardCell = BoardCell;
